import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {FresnelSockets} from "#material/nodes/input/fresnel/definition"
import {fresnelDefinition} from "#material/nodes/input/fresnel/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import {cyclesVec3} from "#material/cycles/helpers"
import type {ThreeReturns} from "#material/sockets/three"
import type {FresnelNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class Fresnel extends MaterialNode<FresnelSockets> {
    public override get definition() {
        return fresnelDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<FresnelSockets>> => {
        const ior = await evaluator.get(this.parameters.inputs.ior)

        const iorValue = ior?.toFloat() ?? threeConvert(this.parameters.settings.ior, threeValue)
        const facValue = threeConvert(this.parameters.settings.fac, threeValue) ?? threeValue(0)

        if (!iorValue) return {fac: facValue}

        const n1 = threeValue(1.0)
        const r0_sqrt = TSL.div(TSL.sub(n1, iorValue), TSL.add(n1, iorValue))
        const r0 = TSL.mul(r0_sqrt, r0_sqrt)
        const NoV = TSL.normalView.z
        const oneMinusNoV = TSL.sub(threeValue(1.0), NoV)
        const oneMinusNoV_2 = TSL.mul(oneMinusNoV, oneMinusNoV)
        const oneMinusNoV_3 = TSL.mul(oneMinusNoV_2, oneMinusNoV)
        const oneMinusNoV_5 = TSL.mul(oneMinusNoV_2, oneMinusNoV_3)
        const tmp1 = TSL.mul(TSL.sub(n1, r0), oneMinusNoV_5)
        const value = TSL.add(r0, tmp1)

        return {fac: value}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<FresnelSockets>> => {
        const fresnelNode: FresnelNode = {
            type: "fresnel",
            inputs: await evaluator.getAll({IOR: this.parameters.inputs.ior, normal: this.parameters.inputs.normal}),
            parameters: {IOR: this.parameters.settings.ior, normal: cyclesVec3(this.parameters.settings.normal)},
            resources: {},
        }

        return {fac: [fresnelNode, "fac"]}
    }
}
